import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import setLanguage from 'next-translate/setLanguage';

// Hooks
import useSubdomain from 'hooks/use-subdomain';
import useTranslationWithFallback from 'hooks/use-translation-with-fallback';

// Utils
import { AUTHENTICATION_ROUTES, isPublicSubdomainPath } from 'utils/subdomain-helpers';
import { getIsRestaurantSlugValid } from 'utils/slug-validation';
import { setLocaleCookie } from 'utils/cookies-helper';

// Store
import { useSelector } from 'react-redux';
import {
  selectAuthenticationInitialized,
  selectHasRestaurant,
  selectIsLoggingIn,
  selectLocale,
  selectUserIsAuthenticated
} from 'store/slices/userSlice';

// Components
import Loader from 'components/loader';


const NavigationGuard = ({ children = null }) => {
  const { lang } = useTranslationWithFallback();

  const router = useRouter();
  const {
    isReady,
    pathname: pathName,
    query
  } = router;

  // Selectors
  const authenticationInitialized = useSelector(selectAuthenticationInitialized);
  const hasRestaurant = useSelector(selectHasRestaurant);
  const isLoggingIn = useSelector(selectIsLoggingIn);
  const locale = useSelector(selectLocale);
  const userIsAuthenticated = useSelector(selectUserIsAuthenticated);

  // Custom hooks
  const subdomain = useSubdomain();
  const isRestaurantSubdomain = subdomain && getIsRestaurantSlugValid(subdomain);

  // Routes
  const isAuthenticationRoute = AUTHENTICATION_ROUTES.includes(pathName);

  // Only specific paths on subdomains are public (e.g., root '/').
  // All other paths remain guarded (e.g., /account-settings, /edit-menu).
  const isPublicRoute = [
    '/404',
    '/order/[orderId]',
    '/restaurant/[slug]'
  ].includes(pathName) ||
    (isRestaurantSubdomain && isPublicSubdomainPath(pathName));

  const isWelcomeRoute = pathName === '/welcome';
  const isPasswordResetRoute = pathName === '/reset-password';

  const preventRedirect = useMemo(() => (pathName === '/login' && Object.keys(query).includes('token')) || !isReady, [isReady, pathName, query]);

  const loaderElement = (
    <div className="full-page-loader-wrapper">
      <Loader />
    </div>
  );

  useEffect(() => {
    if (locale && lang && locale !== lang) {
      const updateLocate = async () => {
        await setLanguage(locale);
      };

      updateLocate();
      setLocaleCookie(locale);
    }
  }, [lang, locale]);

  if (isPublicRoute) return children;

  if (!authenticationInitialized) return loaderElement;

  if (userIsAuthenticated && !preventRedirect) {
    if (!hasRestaurant && !isWelcomeRoute && !isPasswordResetRoute) {
      router.push('/welcome');

      return loaderElement;
    }

    if (isAuthenticationRoute) {
      router.push('/');

      return loaderElement;
    }

    if (hasRestaurant && isWelcomeRoute) {
      router.push('/');

      return loaderElement;
    }
  } else if (!isAuthenticationRoute && !preventRedirect && !isLoggingIn) {
    router.push('/login');

    return loaderElement;
  }

  return children;
};

NavigationGuard.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)])
};

export default NavigationGuard;
